import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetchEmailAnalysis, fetchEmailTasks, triggerEmailAnalysis, AIEmailAnalysis, AITask } from '../services/api';
import toast from 'react-hot-toast';
import { CpuChipIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const TestAIAnalysis: React.FC = () => {
  const [emailId, setEmailId] = useState<number>(425);
  const queryClient = useQueryClient();

  // 查询AI分析结果
  const { data: aiAnalysis, isLoading: isAnalysisLoading, refetch: refetchAnalysis } = useQuery(
    ['emailAnalysis', emailId],
    () => fetchEmailAnalysis(emailId),
    { 
      enabled: false,
      retry: false,
      onError: () => {
        // 静默处理错误
      }
    }
  );

  // 查询AI任务
  const { data: aiTasks, isLoading: isTasksLoading, refetch: refetchTasks } = useQuery(
    ['emailTasks', emailId],
    () => fetchEmailTasks(emailId),
    { 
      enabled: false,
      retry: false,
      onError: () => {
        // 静默处理错误
      }
    }
  );

  const triggerAnalysisMutation = useMutation(
    (emailId: number) => triggerEmailAnalysis(emailId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['emailAnalysis', emailId]);
        queryClient.invalidateQueries(['emailTasks', emailId]);
        toast.success('AI分析任务已创建');
        // 自动刷新数据
        setTimeout(() => {
          refetchTasks();
        }, 1000);
      },
      onError: () => {
        toast.error('创建AI分析任务失败');
      },
    }
  );

  const handleTriggerAnalysis = () => {
    triggerAnalysisMutation.mutate(emailId);
  };

  const handleRefresh = () => {
    refetchAnalysis();
    refetchTasks();
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">AI分析测试页面</h1>
            
            {/* 控制面板 */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-6">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮件ID:
                  </label>
                  <input
                    type="number"
                    value={emailId}
                    onChange={(e) => setEmailId(Number(e.target.value))}
                    className="w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <button
                  onClick={handleTriggerAnalysis}
                  disabled={triggerAnalysisMutation.isLoading}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  {triggerAnalysisMutation.isLoading ? '分析中...' : '触发AI分析'}
                </button>
                
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  刷新数据
                </button>
              </div>
            </div>

            {/* AI分析结果 */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center mb-4">
                  <CpuChipIcon className="h-5 w-5 text-purple-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">AI分析结果</h3>
                  {isAnalysisLoading && (
                    <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  )}
                </div>

                {aiAnalysis ? (
                  <div className="space-y-4">
                    {/* 分析摘要 */}
                    {aiAnalysis.summary && (
                      <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                        <h4 className="font-medium text-purple-900 mb-2">AI摘要</h4>
                        <p className="text-purple-800">{aiAnalysis.summary}</p>
                      </div>
                    )}

                    {/* 翻译内容 */}
                    {aiAnalysis.translated_content && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                        <h4 className="font-medium text-blue-900 mb-2">AI翻译</h4>
                        <p className="text-blue-800">{aiAnalysis.translated_content}</p>
                      </div>
                    )}

                    {/* 情感分析和分类 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {aiAnalysis.sentiment && (
                        <div className="bg-green-50 border border-green-200 rounded-md p-4">
                          <h4 className="font-medium text-green-900 mb-2">情感分析</h4>
                          <p className="text-green-800">{aiAnalysis.sentiment}</p>
                        </div>
                      )}

                      {aiAnalysis.category && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4">
                          <h4 className="font-medium text-indigo-900 mb-2">AI分类</h4>
                          <p className="text-indigo-800">{aiAnalysis.category}</p>
                        </div>
                      )}
                    </div>

                    {/* 标签和优先级 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {aiAnalysis.tags && aiAnalysis.tags.length > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                          <h4 className="font-medium text-gray-900 mb-2">AI标签</h4>
                          <div className="flex flex-wrap gap-2">
                            {aiAnalysis.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {aiAnalysis.priority_score !== undefined && (
                        <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
                          <h4 className="font-medium text-orange-900 mb-2">优先级评分</h4>
                          <p className="text-orange-800">{aiAnalysis.priority_score}/10</p>
                        </div>
                      )}
                    </div>

                    {/* 建议回复 */}
                    {aiAnalysis.suggested_reply && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <h4 className="font-medium text-yellow-900 mb-2">AI建议回复</h4>
                        <p className="text-yellow-800 mb-2">{aiAnalysis.suggested_reply}</p>
                        {aiAnalysis.reply_tone && (
                          <p className="text-sm text-yellow-700">建议语调: {aiAnalysis.reply_tone}</p>
                        )}
                      </div>
                    )}

                    {/* 置信度分数 */}
                    {aiAnalysis.confidence_scores && Object.keys(aiAnalysis.confidence_scores).length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                        <h4 className="font-medium text-gray-900 mb-2">置信度分数</h4>
                        <div className="space-y-2">
                          {Object.entries(aiAnalysis.confidence_scores).map(([key, value]) => (
                            <div key={key} className="flex justify-between items-center">
                              <span className="text-sm text-gray-700">{key}:</span>
                              <span className="text-sm font-medium text-gray-900">
                                {(value * 100).toFixed(1)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Debug信息 */}
                    <details className="bg-gray-50 border border-gray-200 rounded-md p-4">
                      <summary className="cursor-pointer font-medium text-gray-900">
                        查看原始数据 (Debug)
                      </summary>
                      <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">
                        {JSON.stringify(aiAnalysis, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                    <p className="text-gray-600">暂无AI分析结果</p>
                  </div>
                )}

                {/* AI任务状态 */}
                {aiTasks && aiTasks.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-3">AI任务状态 (Debug)</h4>
                    <div className="space-y-2">
                      {aiTasks.map((task) => (
                        <div
                          key={task.id}
                          className={`border rounded-md p-3 text-sm ${
                            task.status === 'COMPLETED'
                              ? 'bg-green-50 border-green-200'
                              : task.status === 'FAILED'
                              ? 'bg-red-50 border-red-200'
                              : task.status === 'RUNNING'
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-yellow-50 border-yellow-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">
                              任务 #{task.id} - {task.task_type}
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                task.status === 'COMPLETED'
                                  ? 'bg-green-100 text-green-800'
                                  : task.status === 'FAILED'
                                  ? 'bg-red-100 text-red-800'
                                  : task.status === 'RUNNING'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {task.status}
                            </span>
                          </div>
                          
                          {task.error_message && (
                            <div className="flex items-start mt-2">
                              <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-red-700 text-xs">{task.error_message}</p>
                            </div>
                          )}
                          
                          <div className="mt-2 text-xs text-gray-600">
                                                              <p>创建时间: {(() => {
                  let date: Date;
                  if (task.created_at.includes('T') && !task.created_at.includes('Z') && !task.created_at.includes('+')) {
                    date = new Date(task.created_at + 'Z');
                  } else {
                    date = new Date(task.created_at);
                  }
                  return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
                })()}</p>
                {task.completed_at && (
                  <p>完成时间: {(() => {
                    let date: Date;
                    if (task.completed_at.includes('T') && !task.completed_at.includes('Z') && !task.completed_at.includes('+')) {
                      date = new Date(task.completed_at + 'Z');
                    } else {
                      date = new Date(task.completed_at);
                    }
                    return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
                  })()}</p>
                )}
                          </div>

                          {/* Debug信息 */}
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                              查看详细信息
                            </summary>
                            <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-900 dark:text-gray-900">
                              <div className="mb-2">
                                <strong>输入数据:</strong>
                                <pre className="mt-1 whitespace-pre-wrap">
                                  {JSON.stringify(task.input_data, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <strong>输出数据:</strong>
                                <pre className="mt-1 whitespace-pre-wrap">
                                  {JSON.stringify(task.output_data, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </details>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestAIAnalysis; 